import * as Sentry from '@sentry/vue'
import store from '@/vuex/store'
import PresentationApi from '@/api/presentation'
import Socket from '@/socket'
import textUtils from '@/utils/textUtil'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'

const checkPresentationHasResults = (slides) => {
  const { questions, presentation } = store.state
  if (!slides || !questions || !presentation) return false
  const {
    numberOfHearts, numberOfLaughs, numberOfLikes, numberOfSads, numberOfWows,
  } = presentation

  // has reactions
  if (numberOfHearts > 0 || numberOfLaughs > 0 || numberOfLikes > 0 ||
      numberOfSads > 0 || numberOfWows > 0) return true

  // has qna questions
  if (questions.length > 0) return true

  // has votes
  if (slides.some((slide) => slide.multipleCountAnswer > 0)) return true

  // has submits
  const submitSlideTypes = ['openEnded', 'ideas', 'wordCloud']
  const allSubmitSlides = slides.filter((slide) => submitSlideTypes.includes(slide.type))
  const someSlidesHaveSubmissions =
    allSubmitSlides.some((slide) => slide.SlideOptions && slide.SlideOptions.length !== 0)
  if (someSlidesHaveSubmissions) return true

  return false
}

const updatePresentationHasResults = async (presentation, payload) => {
  await PresentationApi.updatePresentationByPresentationId(presentation.id,
    { hasResults: payload })
}

const checkAndUpdatePresentationHasResults = async () => {
  const { presentation } = store.state
  if (!presentation || !presentation.slides || presentation.slides.length === 0 ||
    !presentation.hasResults) return

  if (!checkPresentationHasResults(presentation.slides)) {
    updatePresentationHasResults(presentation, false)
    store.state.presentation.hasResults = false
  }
}

const needSuggestPresentationTitle = () => {
  const { presentation } = store.state
  const defaultName = 'My new presentation'
  return (!presentation.name || presentation.name === defaultName) && !presentation.hasAutoSuggestedTitle
}

const _getSlideTitle = (title) => {
  if (!title || !title.length) return false
  const defaultTitles = ['Click to add question', 'Click to add heading']
  if (defaultTitles.includes(title.trim())) return null
  return title.trim()
}
const _getCanvasBlockTitle = (canvasBlocks) => {
  if (!canvasBlocks) return false
  return textUtils.getFirstCanvasBlockContent(canvasBlocks)
}

const _getFirstSlideTitle = (slide) => {
  if (slide.type !== 'freestyle') {
    return _getSlideTitle(slide.title)
  }
  return _getCanvasBlockTitle(slide.canvasBlocks)
}

const getFirstSlideTitleIfExisted = () => {
  const { presentation } = store.state
  let firstSlideTitle = null
  if (!presentation.slides || !presentation.slides.length) return null
  const filteredSlides = presentation.slides.filter((s) => !s.metadata || !s.metadata.isImportedFromPPT)
  for (let i = 0; i < filteredSlides.length; i++) {
    firstSlideTitle = _getFirstSlideTitle(filteredSlides[i])
    if (firstSlideTitle) break
  }
  return firstSlideTitle
}

const handleSuggestPresentationTitle = async (title) => {
  try {
    if (!needSuggestPresentationTitle()) return
    title = getFirstSlideTitleIfExisted()
    if (!title || !title.trim().length) return

    store.dispatch('setSyncLoadingAction', 1)
    const isFullSentence = title.indexOf('.') > 0
    const firstSentence = isFullSentence ? title.split('.')[0].trim().substring(0, 100) : title.trim().substring(0, 100)
    await PresentationApi.updatePresentationByPresentationId(store.state.presentation.id, {
      name: firstSentence,
      hasAutoSuggestedTitle: true,
      socketId: Socket.getSocketId(),
    })
    store.dispatch('patchPresentationAction', {
      name: firstSentence,
      hasAutoSuggestedTitle: true,
    })

    store.dispatch('setSyncLoadingAction', 2)
    utils.mixpanelTracking(
      eventCategories.PRESENTATION_EDITOR,
      'Auto-suggest presentation title',
      firstSentence,
      null,
      {
        presentationId: store.state.presentation.id,
      },
    )
  } catch (error) {
    Sentry.captureException(error)
    store.dispatch('setSyncLoadingAction', 2)
  }
}

// AHA-33599: Track presenting mode issues
const trackPresentingModeIssues = (presentation) => {
  try {
    const isFromTemplate = !!presentation.sourcePresentation

    // Track unexpected presenting mode from templates
    if (presentation.presenting === true && isFromTemplate) {
      const trackingData = {
        presentationId: presentation.id,
        sourcePresentation: presentation.sourcePresentation,
        presenting: presentation.presenting,
        takenOverBy: presentation.takenOverBy,
        issueType: 'template_unexpected_presenting',
        isFromTemplate,
        userAction: 'load_presentation_detail',
        hasSourcePresentation: !!presentation.sourcePresentation,
      }

      Sentry.captureMessage('Template with unexpected presenting mode detected', {
        level: 'warning',
        extra: trackingData,
      })
    }
  } catch (error) {
    Sentry.captureException(error)
  }
}

export default {
  checkPresentationHasResults,
  updatePresentationHasResults,
  checkAndUpdatePresentationHasResults,
  handleSuggestPresentationTitle,
  needSuggestPresentationTitle,
  trackPresentingModeIssues,
}
